/**
 * Data for programmatic hub pages.
 *
 * Hub page types:
 *   /top-[year]          – condos by T.O.P year (2025–2032)
 *   /near-[station]-mrt  – condos near a specific MRT station
 *   /near-[school]       – condos near a primary/secondary school
 *   /freehold            – freehold new launches
 *   /99-year-leasehold   – 99-year leasehold new launches
 *   /under-1-5-million   – new launches priced under S$1.5 million
 *   /under-2-million     – new launches priced under S$2 million
 *   /under-3-million     – new launches priced under S$3 million
 *
 * Pure data file — no backend / DB. Add a new entry here and the page,
 * meta, breadcrumb, JSON-LD, and sitemap entry all generate automatically.
 */

// ─── TOP year hubs ────────────────────────────────────────────────────────────

export const TOP_YEARS = [2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032];

export function isTopSlug(slug: string): number | null {
  const m = slug.match(/^top-(\d{4})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  return TOP_YEARS.includes(year) ? year : null;
}

// ─── MRT station hubs ─────────────────────────────────────────────────────────

export interface MrtStation {
  slug: string;
  label: string;
  districts: string[];
  description: string;
}

export const MRT_STATIONS: MrtStation[] = [
  // ── Thomson–East Coast Line (TEL) ────────────────────────────────────────
  {
    slug: "lentor",
    label: "Lentor",
    districts: ["D20", "D26"],
    description:
      "Lentor MRT (TEL) sits in Upper Thomson, a few stops from Caldecott interchange. The surrounding Lentor Hills estate hosts several new launches — including Lentor Modern, Lentor Gardens, Lentor Hills Residences, and Lentoria — making it one of Singapore's most active new-launch corridors.",
  },
  {
    slug: "upper-thomson",
    label: "Upper Thomson",
    districts: ["D26"],
    description:
      "Upper Thomson MRT (TEL) connects the Upper Thomson Road F&B enclave to Orchard and the CBD. The quiet, low-density surroundings, proximity to the Central Catchment nature reserve, and good school access attract buyers seeking a nature-adjacent lifestyle.",
  },
  {
    slug: "springleaf",
    label: "Springleaf",
    districts: ["D26"],
    description:
      "Springleaf MRT (TEL) is set within the low-density Springleaf and Upper Thomson estate. The station is surrounded by nature reserves and low-rise landed housing, and the TEL connection to Orchard and Marina Bay has sharpened investor interest in the area.",
  },
  {
    slug: "mayflower",
    label: "Mayflower",
    districts: ["D26"],
    description:
      "Mayflower MRT (TEL) sits between Caldecott interchange and Springleaf in the Upper Thomson corridor. The station serves the Mayflower and Ang Mo Kio Hill residential estate — a mix of HDB upgrader and private condo demand — and is one stop from Caldecott where the CCL connects to Bishan, HarbourFront, and Marina Bay. New launches near Mayflower appeal to buyers seeking a quiet, landed-estate character with strong TEL connectivity to Orchard Road.",
  },
  {
    slug: "bright-hill",
    label: "Bright Hill",
    districts: ["D20", "D26"],
    description:
      "Bright Hill MRT (TEL) sits at the edge of the Ang Mo Kio and Upper Thomson residential belt. The station is close to MacRitchie Reservoir and the nature reserve access points, and gives residents a green, low-density address with direct city access.",
  },
  {
    slug: "caldecott",
    label: "Caldecott",
    districts: ["D20", "D26"],
    description:
      "Caldecott MRT interchange (CCL + TEL) connects the Upper Thomson and Bishan corridors. The TEL extension puts Orchard Road 10 minutes away; the CCL branch reaches the Marina Bay financial district via Stadium and Bayfront.",
  },
  {
    slug: "tanjong-rhu",
    label: "Tanjong Rhu",
    districts: ["D15"],
    description:
      "Tanjong Rhu MRT (TEL) opens up the Tanjong Rhu waterfront enclave — a niche, low-density precinct between Marina Bay and Katong. The station gives residents direct access to Orchard and the CBD without driving.",
  },
  {
    slug: "marine-parade",
    label: "Marine Parade",
    districts: ["D15"],
    description:
      "Marine Parade MRT (TEL) finally connects Singapore's eastern heritage belt — Katong, Joo Chiat, and Marine Parade — to the island-wide rail network. East Coast Park, Parkway Parade, and the Tanjong Katong heritage shophouse precinct are all walkable.",
  },
  {
    slug: "stevens",
    label: "Stevens",
    districts: ["D10", "D11"],
    description:
      "Stevens MRT interchange (DTL + TEL) connects the Tanglin and Bukit Timah prime districts to both Orchard and the Botanic Gardens. The surrounding Singapore Botanic Gardens UNESCO site and the prime school cluster make this a top-end residential address.",
  },
  {
    slug: "great-world",
    label: "Great World",
    districts: ["D9"],
    description:
      "Great World MRT (TEL) serves the River Valley residential enclave. Great World City mall, Robertson Quay, and easy access to the CBD make this a sought-after address for professionals and expat tenants, driving strong leasing yields for investors.",
  },
  {
    slug: "havelock",
    label: "Havelock",
    districts: ["D3", "D9"],
    description:
      "Havelock MRT (TEL) sits at the edge of the River Valley and Robertson Quay enclave. The station gives the Pearl's Hill and Chinatown fringe residential pockets direct access to Orchard Road and the East Coast.",
  },
  // ── North-South Line (NSL) ────────────────────────────────────────────────
  {
    slug: "orchard",
    label: "Orchard",
    districts: ["D9"],
    description:
      "Orchard MRT (NSL + TEL) is Singapore's premium retail and luxury residential address. ION Orchard, Takashimaya, and Paragon sit above the station, and the surrounding River Valley and Killiney catchment hosts several ultra-luxury new launches.",
  },
  {
    slug: "newton",
    label: "Newton",
    districts: ["D11"],
    description:
      "Newton MRT interchange (NSL + DTL) puts residents 2 stops from Orchard Road and 4 stops from Raffles Place. Newton Food Centre, the Good Class Bungalow estate on Ridout Road, and proximity to top schools give this node a strong luxury-upgrader profile.",
  },
  {
    slug: "novena",
    label: "Novena",
    districts: ["D11"],
    description:
      "Novena MRT (NSL) sits beside Singapore's medical hub — Mount Elizabeth Novena, Tan Tock Seng, and Novena Specialist Centre. United Square, Velocity, and Novena Square are the retail anchors, and the area attracts healthcare professionals and city-adjacent upgraders.",
  },
  {
    slug: "toa-payoh",
    label: "Toa Payoh",
    districts: ["D12"],
    description:
      "Toa Payoh MRT (NSL) anchors one of Singapore's most established HDB and condo estates. Toa Payoh Hub, Balestier Plaza, and an excellent school cluster — including CHIJ Primary (Toa Payoh) and Pei Chun — make this a perennial upgrader favourite.",
  },
  {
    slug: "braddell",
    label: "Braddell",
    districts: ["D12", "D20"],
    description:
      "Braddell MRT (NSL) connects the Toa Payoh and Bishan residential belts. The area hosts a mix of condos and landed housing and is within the 1-km catchment of several top primary schools along Braddell Road and Lorong Chuan.",
  },
  {
    slug: "bishan",
    label: "Bishan",
    districts: ["D20"],
    description:
      "Bishan MRT interchange (NSL + CCL) gives residents fast access to both Raffles Place and Marina Bay. Bishan-Ang Mo Kio Park, Junction 8, and the strong primary school cluster — Ai Tong, Catholic High, CHIJ St. Nicholas — fuel sustained demand.",
  },
  {
    slug: "ang-mo-kio",
    label: "Ang Mo Kio",
    districts: ["D20"],
    description:
      "Ang Mo Kio MRT (NSL) sits in the heart of one of Singapore's most established mature estates. AMK Hub, the town centre hawker cluster, and the popular primary school catchment zone make this a perennial upgrader and investor favourite.",
  },
  {
    slug: "yishun",
    label: "Yishun",
    districts: ["D27"],
    description:
      "Yishun MRT (NSL) is the hub of Singapore's northern regional centre. Northpoint City, Khoo Teck Puat Hospital, and Yishun Park Hawker Centre anchor the estate, with several new launch condos in the surrounding Yishun Ave clusters.",
  },
  {
    slug: "khatib",
    label: "Khatib",
    districts: ["D27", "D28"],
    description:
      "Khatib MRT (NSL) sits between Yishun and Yio Chu Kang. The station serves the Khatib low-density residential estate and the Upper Seletar Reservoir corridor — a quiet, green address with direct NSL access to Orchard Road and Raffles Place.",
  },
  {
    slug: "yio-chu-kang",
    label: "Yio Chu Kang",
    districts: ["D28"],
    description:
      "Yio Chu Kang MRT (NSL) anchors Singapore's north-eastern low-density corridor. Greenwich V, The Seletar Mall, and the Jalan Kayu F&B enclave are nearby, and the surrounding area features a large supply of terrace and semi-D housing.",
  },
  {
    slug: "woodlands",
    label: "Woodlands",
    districts: ["D25"],
    description:
      "Woodlands MRT (NSL) is Singapore's northern gateway and a key interchange on the Thomson–East Coast and Jurong Region lines. Causeway Point, Woodlands Square, and the cross-border RTS link to Johor Bahru are within walking distance.",
  },
  {
    slug: "admiralty",
    label: "Admiralty",
    districts: ["D25"],
    description:
      "Admiralty MRT (NSL) anchors the northern fringe between Woodlands and Sembawang. Admiralty Mall and the Sembawang Park waterfront serve the immediate neighbourhood; the NSL delivers residents to Orchard Road in approximately 35 minutes. New launch condos in D25 typically offer larger units at more accessible price points than central Singapore, attracting HDB upgraders and families.",
  },
  {
    slug: "sembawang",
    label: "Sembawang",
    districts: ["D27"],
    description:
      "Sembawang MRT (NSL) serves Singapore's northern coast. Sun Plaza, Sembawang Shopping Centre, and the Sembawang Park waterfront are local anchors, and the district has seen a steady pipeline of new executive condo and private launches.",
  },
  {
    slug: "bukit-batok",
    label: "Bukit Batok",
    districts: ["D23"],
    description:
      "Bukit Batok MRT (EWL) anchors the Bukit Batok estate with West Mall, Bukit Batok Nature Park, and the Little Guilin quarry park as lifestyle draws. The area has seen renewed interest from buyers following the Tengah eco-town launch nearby.",
  },
  // ── East-West Line (EWL) ─────────────────────────────────────────────────
  {
    slug: "queenstown",
    label: "Queenstown",
    districts: ["D3"],
    description:
      "Queenstown MRT (EWL) sits in one of Singapore's oldest estates, now revitalised by the one-north biotech park nearby and the steady regeneration of the Queenstown precinct. IKEA Alexandra, Anchorpoint, and Queensway Shopping Centre are the main retail anchors.",
  },
  {
    slug: "commonwealth",
    label: "Commonwealth",
    districts: ["D3"],
    description:
      "Commonwealth MRT (EWL) anchors the Commonwealth Drive and Tanglin Halt residential belt. The Tanglin Halt estate redevelopment by HDB and proximity to one-north have driven renewed interest from upgraders in this mature D3 estate.",
  },
  {
    slug: "redhill",
    label: "Redhill",
    districts: ["D3"],
    description:
      "Redhill MRT (EWL) connects the Alexandra and Queenstown residential fringe to the CBD in 3 stops. The surrounding Alexandra Distripark, Redhill Food Centre, and Metropolis office park make this a strong rental-demand node.",
  },
  {
    slug: "tiong-bahru",
    label: "Tiong Bahru",
    districts: ["D3"],
    description:
      "Tiong Bahru MRT (EWL) serves Singapore's most sought-after heritage neighbourhood. The Tiong Bahru Plaza retail cluster, Art Deco walkups, independent cafes, and Tiong Bahru Bakery draw a creative professional tenant base that sustains strong leasing yields.",
  },
  {
    slug: "outram-park",
    label: "Outram Park",
    districts: ["D2", "D3"],
    description:
      "Outram Park MRT interchange (EWL + NEL + TEL) is one of Singapore's best-connected nodes. Serving Chinatown, Tanjong Pagar, and the Pearl's Hill area, it gives residents access to the CBD, HarbourFront, and the east coast on a single interchange.",
  },
  {
    slug: "tanjong-pagar",
    label: "Tanjong Pagar",
    districts: ["D2"],
    description:
      "Tanjong Pagar MRT (EWL) is the gateway to Singapore's second CBD — the Tanjong Pagar-Anson financial cluster. Duxton Hill, Pinnacle@Duxton, and the Wallich Residence luxury tower sit within walking distance, anchoring strong expat and professional demand.",
  },
  {
    slug: "dover",
    label: "Dover",
    districts: ["D5"],
    description:
      "Dover MRT (EWL) sits between Clementi and Buona Vista, giving residents direct access to NUS and the one-north biotech park. The station serves the Dover Forest estate and is a favourite with academic and tech-sector buyers.",
  },
  {
    slug: "buona-vista",
    label: "Buona Vista",
    districts: ["D5"],
    description:
      "Buona Vista MRT interchange (EWL + CCL) is the gateway to one-north, Singapore's biotech and tech park. NUS, Star Vista, and the Holland Village enclave are nearby, attracting tenants and buyers in the education and research sectors.",
  },
  {
    slug: "clementi",
    label: "Clementi",
    districts: ["D5"],
    description:
      "Clementi MRT (EWL) sits at the crossroads of NUS, the West Coast residences, and Clementi Mall. The strong student and academic tenant base and established amenities anchor steady leasing demand across all unit types.",
  },
  {
    slug: "jurong-east",
    label: "Jurong East",
    districts: ["D22"],
    description:
      "Jurong East MRT interchange (EWL + NSL) is the gateway to Jurong Lake District — Singapore's planned second CBD. JEM, Westgate, and IMM sit at the interchange; the long-term URA transformation pipeline is a significant price catalyst.",
  },
  {
    slug: "boon-lay",
    label: "Boon Lay",
    districts: ["D22"],
    description:
      "Boon Lay MRT (EWL) anchors the Jurong West residential belt. Jurong Point (one of Singapore's largest suburban malls), Boon Lay hawker centre, and strong connectivity to NTU and Jurong Island make this a consistent upgrader and investment node.",
  },
  {
    slug: "lakeside",
    label: "Lakeside",
    districts: ["D22"],
    description:
      "Lakeside MRT (EWL) sits beside Jurong Lake Gardens and the emerging Jurong Lake District. The Chinese Garden heritage park, Jurong Bird Park's Mandai relocation, and URA's second CBD plans around the lake give this node long-term capital upside.",
  },
  {
    slug: "aljunied",
    label: "Aljunied",
    districts: ["D14"],
    description:
      "Aljunied MRT (EWL) connects the Geylang and Paya Lebar residential fringe to the CBD. Paya Lebar Quarter sits two stops east; the surrounding Geylang Serai heritage and industrial cluster gives this area an active mixed-use character.",
  },
  {
    slug: "kembangan",
    label: "Kembangan",
    districts: ["D16"],
    description:
      "Kembangan MRT (EWL) serves the low-density Opera Estate and Kembangan enclave in Bedok. The Red Swastika and Opera Estate Primary school catchments, proximity to East Coast Park, and a quiet landed neighbourhood make this a steady family-buyer choice.",
  },
  {
    slug: "tanah-merah",
    label: "Tanah Merah",
    districts: ["D16", "D17"],
    description:
      "Tanah Merah MRT interchange (EWL) serves the upper Bedok and Changi fringe. Bedok Reservoir, the Changi Business Park, and East Village retail cluster are nearby; the interchange branches to Expo and Changi Airport.",
  },
  {
    slug: "simei",
    label: "Simei",
    districts: ["D18"],
    description:
      "Simei MRT (EWL) anchors the Simei and Tampines fringe residential belt. Eastpoint Mall, Changi General Hospital, and easy access to Changi Airport give this node a practical, family-oriented character.",
  },
  {
    slug: "pasir-ris",
    label: "Pasir Ris",
    districts: ["D18"],
    description:
      "Pasir Ris MRT (EWL) serves Singapore's far-eastern residential estate. Downtown East, Elias Mall, Pasir Ris Beach, and Pasir Ris Park anchor a family-heavy market that has seen renewed activity following new GLS sites near the town centre.",
  },
  {
    slug: "lavender",
    label: "Lavender",
    districts: ["D7", "D8"],
    description:
      "Lavender MRT (EWL) connects the Kallang and Jalan Besar residential pockets to the CBD in 2 stops. The surrounding heritage shophouse belt, Jalan Besar Stadium, and proximity to Bugis Junction make this an emerging creative district node.",
  },
  {
    slug: "kallang",
    label: "Kallang",
    districts: ["D12", "D14"],
    description:
      "Kallang MRT (EWL) serves the Kallang industrial and residential belt between the Geylang River and the Singapore Sports Hub. The Kallang Basin Mixed Development is a long-term uplift catalyst for the surrounding residential catchment.",
  },
  // ── North-East Line (NEL) ─────────────────────────────────────────────────
  {
    slug: "harbourfront",
    label: "HarbourFront",
    districts: ["D4"],
    description:
      "HarbourFront MRT interchange (NEL + CCL) is the gateway to Sentosa and the greater Southern Waterfront. VivoCity, Resorts World Sentosa, and the upcoming Greater Southern Waterfront transformation position this as one of Singapore's most watched long-term uplift zones.",
  },
  {
    slug: "little-india",
    label: "Little India",
    districts: ["D8"],
    description:
      "Little India MRT interchange (NEL + DTL) serves the Rochor and Farrer Park residential belt. Mustafa Centre, Tekka Market, and City Square Mall anchor the area, and the Farrer Park Hospital and Connexion mixed development add a healthcare cluster draw.",
  },
  {
    slug: "farrer-park",
    label: "Farrer Park",
    districts: ["D8"],
    description:
      "Farrer Park MRT (NEL) sits beside the Connexion mixed development and Farrer Park Hospital. The surrounding Farrer Park residential belt gives buyers access to both the CBD and Orchard Road, with strong leasing demand from medical professionals.",
  },
  {
    slug: "boon-keng",
    label: "Boon Keng",
    districts: ["D12"],
    description:
      "Boon Keng MRT (NEL) serves the Boon Keng and Bendemeer residential belt between Little India and Serangoon. The surrounding landed housing estate, Bendemeer Primary school catchment, and 3-stop access to Marina Bay make this a quiet value node.",
  },
  {
    slug: "potong-pasir",
    label: "Potong Pasir",
    districts: ["D13"],
    description:
      "Potong Pasir MRT (NEL) sits between Serangoon and Boon Keng. The Bidadari estate redevelopment, a low-rise residential character, and proximity to the Woodleigh TEL station make this quiet node a sleeper pick for value buyers.",
  },
  {
    slug: "woodleigh",
    label: "Woodleigh",
    districts: ["D13"],
    description:
      "Woodleigh MRT interchange (NEL + TEL) anchors the Bidadari estate — Singapore's newest HDB-led estate built on heritage land. Woodleigh Village mall, Bidadari Park, and the dual-line interchange give this area exceptional connectivity for its size.",
  },
  {
    slug: "serangoon",
    label: "Serangoon",
    districts: ["D13", "D19"],
    description:
      "Serangoon MRT interchange (NEL + CCL) is the gateway to Little India, Hougang, and the North-East. NEX mall, the wet market cluster, and proximity to the Bidadari new estate make this interchange a consistent new-launch demand driver.",
  },
  {
    slug: "kovan",
    label: "Kovan",
    districts: ["D19"],
    description:
      "Kovan MRT (NEL) anchors the Kovan and Upper Serangoon residential belt. The Kovan Heartland Mall cluster, low-rise private housing, and Maris Stella High's 1-km catchment combine to create steady, school-driven demand for properties in this estate.",
  },
  {
    slug: "hougang",
    label: "Hougang",
    districts: ["D19"],
    description:
      "Hougang MRT (NEL) connects to the Serangoon interchange for CBD access. The mature Hougang estate offers a mix of resale and occasional new-launch opportunities surrounded by established hawker centres, schools, and parks.",
  },
  {
    slug: "buangkok",
    label: "Buangkok",
    districts: ["D19"],
    description:
      "Buangkok MRT (NEL) connects the Sengkang–Hougang residential belt directly to the CBD via the NEL interchange at Dhoby Ghaut. The surrounding Seletar Hills landed estate, Punggol Park, and Buangkok Green give this area a quiet, low-density character. New launch condos in D19 near Buangkok appeal to families seeking a green address with NEL city access and access to the D19 school cluster.",
  },
  {
    slug: "sengkang",
    label: "Sengkang",
    districts: ["D19"],
    description:
      "Sengkang MRT (NEL/LRT) is the town centre of one of Singapore's youngest and fastest-growing residential estates. Compass One, Rivervale Plaza, and a dense LRT feeder network serve the surrounding precincts. The Punggol Digital District and Seletar Aerospace Park are nearby employment hubs, supporting strong rental demand from tech and aviation-sector tenants. New launches in D19 around Sengkang are predominantly 99-year leasehold and priced competitively against comparable RCR projects.",
  },
  {
    slug: "punggol",
    label: "Punggol",
    districts: ["D19"],
    description:
      "Punggol MRT (NEL/LRT) anchors Singapore's youngest and fastest-growing estate. The Punggol Digital District, Coney Island, and Northshore waterway housing give this district a strong draw for tech-sector professionals and young families.",
  },
  // ── Circle Line (CCL) ─────────────────────────────────────────────────────
  {
    slug: "dhoby-ghaut",
    label: "Dhoby Ghaut",
    districts: ["D9"],
    description:
      "Dhoby Ghaut MRT interchange (NSL + NEL + CCL) is Singapore's most connected node by line count. Plaza Singapura, The Cathay, and the Orchard Road shopping belt are steps away — a premium address for investors targeting high leasing yields.",
  },
  {
    slug: "macpherson",
    label: "MacPherson",
    districts: ["D13", "D14"],
    description:
      "MacPherson MRT interchange (CCL + DTL) connects the Paya Lebar fringe to the CBD and Buona Vista. The surrounding MacPherson industrial estate is being progressively redeveloped into residential, and Paya Lebar Quarter is two stops away on the DTL.",
  },
  {
    slug: "dakota",
    label: "Dakota",
    districts: ["D14", "D15"],
    description:
      "Dakota MRT (CCL) sits beside the Old Airport Road hawker centre — Singapore's most beloved food destination. The surrounding residential estate offers a rare combination of food culture, park access, and fast CCL connectivity.",
  },
  {
    slug: "mountbatten",
    label: "Mountbatten",
    districts: ["D15"],
    description:
      "Mountbatten MRT (CCL) serves the Katong and Mountbatten private residential belt. The Singapore Sports Hub, Kallang Basin, and the heritage Meyer Road seafront precinct are a short walk away, and the CCL gives direct access to Orchard and HarbourFront.",
  },
  {
    slug: "paya-lebar",
    label: "Paya Lebar",
    districts: ["D14"],
    description:
      "Paya Lebar MRT interchange (EWL + CCL) anchors the Paya Lebar Central commercial hub. Paya Lebar Quarter, PLQ Mall, and Singapore Post Centre give this node a strong anchor, and the surrounding Geylang and Eunos areas offer a range of price points.",
  },
  {
    slug: "lorong-chuan",
    label: "Lorong Chuan",
    districts: ["D20"],
    description:
      "Lorong Chuan MRT (CCL) sits between the Serangoon and Bishan interchanges, giving residents fast CCL access to HarbourFront, Dhoby Ghaut, and Marina Bay. The surrounding Lorong Chuan estate is one of D20's most low-density private residential pockets — dominated by freehold condos and landed housing — and sits within the 1-km catchment of several top D20 primary schools including Zhonghua and St. Gabriel's.",
  },
  // ── Downtown Line (DTL) ──────────────────────────────────────────────────
  {
    slug: "beauty-world",
    label: "Beauty World",
    districts: ["D21"],
    description:
      "Beauty World MRT (DTL) anchors the Upper Bukit Timah retail strip. The Rail Corridor, Bukit Timah Hill, and the Sixth Avenue good school belt sit within a 2-km radius, and the node has seen several new launches since the DTL opened.",
  },
  {
    slug: "hillview",
    label: "Hillview",
    districts: ["D23"],
    description:
      "Hillview MRT (DTL) gives residents in Dairy Farm and Hillview a direct line into the CBD. Hillview Terrace, the Rail Corridor, and Bukit Timah Nature Reserve create a low-density, green living environment popular with families.",
  },
  {
    slug: "bukit-panjang",
    label: "Bukit Panjang",
    districts: ["D23"],
    description:
      "Bukit Panjang MRT interchange (DTL + LRT) connects this western enclave to Jurong and the city centre. Hillion Mall and Junction 10 are the retail anchors, and the area commands relatively affordable prices for freehold and long-lease condos.",
  },
  {
    slug: "one-north",
    label: "one-north",
    districts: ["D5"],
    description:
      "one-north MRT (CCL) sits at the heart of Singapore's biotech and media hub. Fusionopolis, Biopolis, and the Mediapolis cluster surround the station, and the Buona Vista interchange is one stop away — making this Singapore's most tech-professional residential node.",
  },
  {
    slug: "bedok",
    label: "Bedok",
    districts: ["D16"],
    description:
      "Bedok MRT (EWL) is the commercial heart of Singapore's largest residential estate. Bedok Mall, the Bedok hawker village, Bedok Reservoir, and Eastpoint at Simei give residents a comprehensive set of amenities within walking distance.",
  },
  // ── Jurong Region Line (upcoming) + Tengah ───────────────────────────────
  {
    slug: "tengah",
    label: "Tengah",
    districts: ["D24"],
    description:
      "Tengah is Singapore's newest eco-town, built car-lite with a 5 km park corridor as its spine. The upcoming Jurong Region Line stations — Tengah Park, Bukit Batok West, Tengah Plantation — will connect the town to Jurong East interchange from 2027.",
  },
  {
    slug: "choa-chu-kang",
    label: "Choa Chu Kang",
    districts: ["D23"],
    description:
      "Choa Chu Kang MRT interchange (EWL + LRT) anchors the western regional centre. Lot One Shoppers' Mall, the Choa Chu Kang Sports Centre, and the LRT loop serving Bukit Panjang and Tengah make this the hub of Singapore's north-western residential belt.",
  },
  {
    slug: "tampines",
    label: "Tampines",
    districts: ["D18"],
    description:
      "Tampines MRT (EWL/DTL) interchange is the hub of Singapore's eastern regional centre and one of the busiest suburban nodes in the island. Tampines Mall, Tampines One, and Century Square sit within walking distance, and the upcoming Tampines North extension will add further connectivity. New launches in D18 around Tampines attract family upgraders seeking suburban value — larger unit sizes at significantly lower psf than RCR and CCR — with strong school catchments and an established retail ecosystem.",
  },
];

export function getMrtStation(slug: string): MrtStation | null {
  return MRT_STATIONS.find((s) => s.slug === slug) ?? null;
}

export function isMrtSlug(slug: string): string | null {
  const m = slug.match(/^near-(.+)-mrt$/);
  return m ? m[1] : null;
}

// ─── School hubs ──────────────────────────────────────────────────────────────

export interface SchoolHub {
  slug: string;
  label: string;
  level: "primary" | "secondary" | "jc" | "tertiary" | "international";
  districts: string[];
  description: string;
}

export const SCHOOLS: SchoolHub[] = [
  // ── D3 Alexandra / Commonwealth / Queenstown / Tiong Bahru ───────────────
  {
    slug: "queenstown-primary",
    label: "Queenstown Primary School",
    level: "primary",
    districts: ["D3"],
    description:
      "Queenstown Primary is located on Margaret Drive in the Queenstown estate. Its 1-km catchment covers the Tiong Bahru and Commonwealth fringe — an area with a mix of heritage HDB and new freehold condos that command a premium for school proximity.",
  },
  {
    slug: "fairfield-methodist-primary",
    label: "Fairfield Methodist School (Primary)",
    level: "primary",
    districts: ["D3"],
    description:
      "Fairfield Methodist Primary is located at Towner Road in the Queenstown-Tiong Bahru area. Its catchment overlaps with D3, where regeneration projects and proximity to the CBD have driven significant upgrader demand in recent years.",
  },
  {
    slug: "crescent-girls-school",
    label: "Crescent Girls' School",
    level: "secondary",
    districts: ["D3"],
    description:
      "Crescent Girls' School is located on Commonwealth Avenue in the Queenstown estate (D3). One of Singapore's top all-girls secondary schools, it generates school-proximity demand across the Queenstown, Commonwealth, and Tiong Bahru residential belt. D3 condos within the 1-km catchment are predominantly freehold or 999-year tenure — a combination that commands a significant price premium over comparable leasehold units in the surrounding districts.",
  },
  // ── D5 Buona Vista / Clementi / West Coast / one-north ──────────────────
  {
    slug: "nan-hua-primary",
    label: "Nan Hua Primary School",
    level: "primary",
    districts: ["D5"],
    description:
      "Nan Hua Primary School is located on Clementi Avenue 1. One of Singapore's most consistently oversubscribed primary schools, its 1-km priority catchment makes Clementi and West Coast condos a perennial top pick for Chinese-medium school parents.",
  },
  {
    slug: "clementi-primary",
    label: "Clementi Primary School",
    level: "primary",
    districts: ["D5"],
    description:
      "Clementi Primary School sits near Clementi MRT in the heart of D5. Its catchment zone covers the West Coast Drive and Clementi residential belt, and the school's consistent performance makes it a draw for upgrader families in this district.",
  },
  // ── D9 Orchard / River Valley ─────────────────────────────────────────────
  {
    slug: "river-valley-primary",
    label: "River Valley Primary School",
    level: "primary",
    districts: ["D9"],
    description:
      "River Valley Primary School sits on River Valley Road between Orchard and the CBD. The school is one of the most sought-after in the prime district belt, and condos within its 1-km catchment in D9 consistently command a location premium.",
  },
  {
    slug: "singapore-chinese-girls-primary",
    label: "Singapore Chinese Girls' School (Primary)",
    level: "primary",
    districts: ["D9", "D11"],
    description:
      "Singapore Chinese Girls' School (Primary) is located on Dunearn Road. Its 1-km catchment covers parts of Newton and River Valley — among Singapore's most premium private residential addresses — driving strong demand for condos in the zone.",
  },
  {
    slug: "singapore-chinese-girls-school",
    label: "Singapore Chinese Girls' School",
    level: "secondary",
    districts: ["D9", "D11"],
    description:
      "Singapore Chinese Girls' School (Secondary) on Dunearn Road is one of Singapore's most prestigious all-girls schools. Its location at the Newton-River Valley fringe creates a consistent school-proximity premium for condos in D9 and D11.",
  },
  // ── D10 Tanglin / Holland / Bukit Timah ──────────────────────────────────
  {
    slug: "henry-park-primary",
    label: "Henry Park Primary School",
    level: "primary",
    districts: ["D10"],
    description:
      "Henry Park Primary sits on Linden Drive off Holland Road. Its 1-km priority zone covers the Holland Village enclave and parts of Bukit Timah — both premium freehold districts where school proximity is a significant price premium driver.",
  },
  {
    slug: "raffles-girls-primary",
    label: "Raffles Girls' Primary School",
    level: "primary",
    districts: ["D10", "D11"],
    description:
      "Raffles Girls' Primary sits on Braddell Road, with a 1-km catchment covering parts of Novena and Tanglin. It is consistently among the most over-subscribed government primary schools in Singapore.",
  },
  {
    slug: "methodist-girls-school",
    label: "Methodist Girls' School",
    level: "secondary",
    districts: ["D10"],
    description:
      "Methodist Girls' School is located on Blackmore Drive, Bukit Timah. The surrounding Holland and Tanglin enclave is dominated by freehold landed and condo stock, making it a prime area for families seeking school proximity.",
  },
  {
    slug: "nanyang-girls-high",
    label: "Nanyang Girls' High School",
    level: "secondary",
    districts: ["D10"],
    description:
      "Nanyang Girls' High School is along King's Road in Bukit Timah. Together with Hwa Chong Institution next door, it creates a school cluster that anchors some of the highest property premiums in Singapore.",
  },
  {
    slug: "hwa-chong-institution",
    label: "Hwa Chong Institution",
    level: "jc",
    districts: ["D10"],
    description:
      "Hwa Chong Institution sits on Bukit Timah Road, adjacent to Nanyang Girls' High. This cluster of elite schools drives a persistent price premium for condos in the Bukit Timah corridor — one of the best performing segments in Singapore property.",
  },
  // ── D11 Newton / Novena ───────────────────────────────────────────────────
  {
    slug: "acs-primary",
    label: "Anglo-Chinese School (Primary)",
    level: "primary",
    districts: ["D11"],
    description:
      "Anglo-Chinese School (Primary) is located on Barker Road in the Newton-Novena residential belt. ACS (Primary) is among Singapore's most sought-after boys' schools, and its 1-km catchment drives demand for condos along Barker Road, Newton Road, and the D11 fringe.",
  },
  // ── D12 Balestier / Toa Payoh ─────────────────────────────────────────────
  {
    slug: "pei-chun-public-school",
    label: "Pei Chun Public School",
    level: "primary",
    districts: ["D12"],
    description:
      "Pei Chun Public School is located in Toa Payoh (D12). One of Singapore's most consistently oversubscribed SAP primary schools, its Phase 2A1 and 2A2 alumni demand creates persistent parent-buyer pressure across the D12 catchment. Condos and HDB flats within the 1-km radius — particularly along Lorong 1 Toa Payoh and Braddell Road — carry a measurable school-proximity premium over equivalent units outside the zone.",
  },
  {
    slug: "chij-primary-toa-payoh",
    label: "CHIJ Primary (Toa Payoh)",
    level: "primary",
    districts: ["D12"],
    description:
      "CHIJ Primary (Toa Payoh) is located along Ang Mo Kio Avenue 3 at the D12–D20 border. As part of the prestigious CHIJ network, it draws parent-buyers into the Toa Payoh and Braddell catchment zone.",
  },
  {
    slug: "maris-stella-high",
    label: "Maris Stella High School",
    level: "secondary",
    districts: ["D19", "D28"],
    description:
      "Maris Stella High School is located on Upper Serangoon Road in the D19–D28 border zone. One of Singapore's top Catholic boys' secondary schools, it creates school-proximity demand for condos in the Hougang–Kovan belt.",
  },
  // ── D13 MacPherson / Potong Pasir / Bidadari ─────────────────────────────
  {
    slug: "chij-our-lady-of-good-counsel",
    label: "CHIJ Our Lady of Good Counsel",
    level: "primary",
    districts: ["D13"],
    description:
      "CHIJ Our Lady of Good Counsel is situated along Upper Serangoon Road in Hougang. Part of the CHIJ network, the school's Catholic girls' education reputation makes it a demand driver for condos in the D13 and D19 corridor.",
  },
  // ── D14 Eunos / Geylang / Paya Lebar ─────────────────────────────────────
  {
    slug: "kong-hwa-school",
    label: "Kong Hwa School",
    level: "primary",
    districts: ["D14", "D15"],
    description:
      "Kong Hwa School is located on Guillemard Road in the Geylang–Katong area. One of Singapore's top-ranked Chinese-medium primary schools, it is consistently oversubscribed and draws parent-buyers into the D14 and D15 heritage belt.",
  },
  {
    slug: "poi-ching-school",
    label: "Poi Ching School",
    level: "primary",
    districts: ["D14"],
    description:
      "Poi Ching School is located in Eunos, between Paya Lebar and Kembangan. Its 1-km zone covers parts of D14, making the Paya Lebar and Eunos residential pockets attractive for families planning Phase 2B school registration.",
  },
  {
    slug: "dunman-high",
    label: "Dunman High School",
    level: "secondary",
    districts: ["D14", "D15"],
    description:
      "Dunman High School is located along Victoria Street, Tanjong Rhu area. One of Singapore's top Integrated Programme secondary schools, it draws school-proximity buyers into the D15 Marine Parade and Geylang fringe.",
  },
  // ── D15 East Coast / Marine Parade / Katong ───────────────────────────────
  {
    slug: "haig-girls-school",
    label: "Haig Girls' School",
    level: "primary",
    districts: ["D15"],
    description:
      "Haig Girls' School is located on Haig Road in Katong. The school's 1-km catchment covers the heritage Katong–Joo Chiat precinct — one of Singapore's most distinctive and sought-after residential enclaves.",
  },
  {
    slug: "ngee-ann-primary",
    label: "Ngee Ann Primary School",
    level: "primary",
    districts: ["D15"],
    description:
      "Ngee Ann Primary School is located on Marine Parade Road. Its 1-km catchment covers the Marine Parade and Katong residential estate — an area seeing renewed demand following the opening of the TEL Marine Parade MRT station.",
  },
  {
    slug: "tao-nan-school",
    label: "Tao Nan School",
    level: "primary",
    districts: ["D15"],
    description:
      "Tao Nan School is located on Ceylon Road in the Marine Parade area. Its 1-km catchment covers the heritage Katong–Marine Parade enclave, where the new TEL Marine Parade MRT station has accelerated new-launch demand.",
  },
  // ── D16 Bedok / Upper East Coast ─────────────────────────────────────────
  {
    slug: "opera-estate-primary",
    label: "Opera Estate Primary School",
    level: "primary",
    districts: ["D16"],
    description:
      "Opera Estate Primary School is located within the Opera Estate enclave in Bedok (D16). The surrounding estate is one of Singapore's most tranquil low-density private residential pockets — dominated by landed housing and boutique condos — with East Coast Park a short drive away. Its 1-km catchment and the quiet neighbourhood character make D16 condos near Opera Estate particularly popular with families downsizing from landed and upgrader buyers seeking the east-coast lifestyle.",
  },
  {
    slug: "red-swastika-school",
    label: "Red Swastika School",
    level: "primary",
    districts: ["D16"],
    description:
      "Red Swastika School is situated off Bedok North Road. Oversubscription at this well-regarded school drives demand for condos and landed homes in D16's Bedok and Upper East Coast catchment.",
  },
  {
    slug: "temasek-primary",
    label: "Temasek Primary School",
    level: "primary",
    districts: ["D16"],
    description:
      "Temasek Primary School is located in Bedok. A well-regarded SAP school with consistent academic results, its 1-km catchment covers the Bedok North residential belt and is a key demand factor for condos in D16.",
  },
  // ── D18 Pasir Ris / Tampines ─────────────────────────────────────────────
  {
    slug: "st-hildas-primary",
    label: "St. Hilda's Primary School",
    level: "primary",
    districts: ["D18"],
    description:
      "St. Hilda's Primary School is located on Tampines Street 41 in D18. The school's consistent academic performance, mission-school ethos, and active alumni draw attract Catholic families specifically seeking the 1-km registration priority. The surrounding Tampines estate is Singapore's largest, served by the Tampines MRT interchange (EWL/DTL) and anchored by three major malls. New launches near St. Hilda's cater to family buyers who value both school proximity and the comprehensive amenities of the Tampines Regional Centre.",
  },
  // ── D19 Hougang / Punggol / Sengkang ─────────────────────────────────────
  {
    slug: "rosyth-school",
    label: "Rosyth School",
    level: "primary",
    districts: ["D19"],
    description:
      "Rosyth School is situated in Hougang. One of Singapore's consistently top-performing primary schools, it draws parent-buyers into D19 — specifically the Hougang and Serangoon node — in search of 1-km priority registration.",
  },
  {
    slug: "holy-innocents-primary",
    label: "Holy Innocents' Primary School",
    level: "primary",
    districts: ["D19"],
    description:
      "Holy Innocents' Primary is located along Upper Serangoon Road, Hougang. Its consistent over-subscription and strong community reputation create an additional demand layer for condos in the D19 school belt.",
  },
  {
    slug: "nan-chiau-primary",
    label: "Nan Chiau Primary School",
    level: "primary",
    districts: ["D19"],
    description:
      "Nan Chiau Primary School is located in Sengkang. One of Singapore's top-ranked Chinese-medium primary schools, it is among the most oversubscribed schools in the north-east and a major factor behind D19 property demand from parent-buyers.",
  },
  {
    slug: "pei-hwa-presbyterian-primary",
    label: "Pei Hwa Presbyterian Primary School",
    level: "primary",
    districts: ["D19"],
    description:
      "Pei Hwa Presbyterian Primary is located on Punggol Walk. As Punggol's newest estate grows, the school is one of the primary draws for parents buying in the Punggol Digital District and waterway housing precincts.",
  },
  // ── D20 Ang Mo Kio / Bishan / Thomson ────────────────────────────────────
  {
    slug: "anderson-primary",
    label: "Anderson Primary School",
    level: "primary",
    districts: ["D20"],
    description:
      "Anderson Primary School is located in Ang Mo Kio Avenue 9. Its 1-km catchment zone covers parts of Ang Mo Kio and the border of D20, making Bishan and Upper Thomson condos popular with parents planning early enrollment.",
  },
  {
    slug: "ai-tong-school",
    label: "Ai Tong School",
    level: "primary",
    districts: ["D20"],
    description:
      "Ai Tong School sits off Bishan Road. Its 1-km catchment is entirely within D20, making Bishan and Ang Mo Kio condos the default choice for parents seeking proximity to this oversubscribed primary school.",
  },
  {
    slug: "chongfu-school",
    label: "Chongfu School",
    level: "primary",
    districts: ["D20"],
    description:
      "Chongfu School is located on Ang Mo Kio Street 61. Another top-ranked SAP primary school in D20, it contributes to the cluster effect — alongside Ai Tong, Catholic High, and CHIJ St. Nicholas — that keeps D20 among Singapore's most school-driven property markets.",
  },
  {
    slug: "catholic-high-school",
    label: "Catholic High School",
    level: "secondary",
    districts: ["D20"],
    description:
      "Catholic High School is along Bishan Street 22. The 1-km catchment covers the Bishan MRT node — a key demand driver for new launches in D20 that serve both primary and secondary school proximity needs.",
  },
  {
    slug: "chij-st-nicholas-girls",
    label: "CHIJ St. Nicholas Girls' School",
    level: "secondary",
    districts: ["D20"],
    description:
      "CHIJ St. Nicholas Girls' is located on Ang Mo Kio Avenue 3. Together with Catholic High and Ai Tong, it forms a cluster of top schools in D20 that collectively support sustained property demand in the Bishan and Ang Mo Kio area.",
  },
  {
    slug: "raffles-institution",
    label: "Raffles Institution",
    level: "jc",
    districts: ["D20"],
    description:
      "Raffles Institution (JC) is located in Bishan. Singapore's most prestigious junior college, its presence in D20 — alongside Catholic High and the CHIJ cluster — reinforces Bishan's position as one of Singapore's top school-belt residential markets.",
  },
  {
    slug: "eunoia-junior-college",
    label: "Eunoia Junior College",
    level: "jc",
    districts: ["D20"],
    description:
      "Eunoia Junior College is located in Bishan, close to Bishan MRT. The newest of Singapore's top JCs, its presence further strengthens the already formidable D20 school cluster — a key driver of premium property demand in the Bishan-Ang Mo Kio belt.",
  },
  // ── D21 Clementi Park / Upper Bukit Timah ────────────────────────────────
  {
    slug: "nanyang-primary",
    label: "Nanyang Primary School",
    level: "primary",
    districts: ["D21"],
    description:
      "Nanyang Primary School is off King's Road in Upper Bukit Timah. It is one of Singapore's most sought-after primary schools; condos within its 1-km catchment in D21 consistently command a price premium over comparable units outside the zone.",
  },
  {
    slug: "bukit-timah-primary",
    label: "Bukit Timah Primary School",
    level: "primary",
    districts: ["D21"],
    description:
      "Bukit Timah Primary School is situated in Upper Bukit Timah. Its 1-km catchment covers the Sixth Avenue and King Albert Park residential belt — an area dominated by freehold condos and landed housing that commands some of Singapore's highest price-per-sqft figures.",
  },
  // ── D22 Boon Lay / Jurong ─────────────────────────────────────────────────
  {
    slug: "rulang-primary",
    label: "Rulang Primary School",
    level: "primary",
    districts: ["D22"],
    description:
      "Rulang Primary School is located in Jurong West. One of Singapore's most consistently oversubscribed primary schools, it draws parent-buyers into the D22 Jurong residential belt — particularly the condo precincts around Boon Lay and Lakeside.",
  },
  {
    slug: "lianhua-primary",
    label: "Lianhua Primary School",
    level: "primary",
    districts: ["D22"],
    description:
      "Lianhua Primary School is located in Jurong West. The school's proximity to the emerging Jurong Lake District and the Jurong East MRT interchange makes D22 condos within its 1-km catchment attractive for parent-investors banking on long-term URA uplift.",
  },
  // ── D27 Sembawang / Yishun ────────────────────────────────────────────────
  {
    slug: "canberra-primary",
    label: "Canberra Primary School",
    level: "primary",
    districts: ["D27"],
    description:
      "Canberra Primary School is located in the Canberra estate in Sembawang (D27). Serving one of Singapore's fastest-growing northern residential precincts, the school sits amid a mix of HDB BTO, executive condominiums, and new launch private condos. The area is well-served by Canberra MRT (NSL), Wellington Circle connectivity, and proximity to the upcoming Sembawang waterfront masterplan — making it attractive for young families banking on long-term northern corridor uplift.",
  },
  // ── D28 Seletar / Yio Chu Kang ───────────────────────────────────────────
  {
    slug: "jing-shan-primary",
    label: "Jing Shan Primary School",
    level: "primary",
    districts: ["D19", "D28"],
    description:
      "Jing Shan Primary School is located in the Buangkok Green precinct of Sengkang (D19/D28 border). Its SAP status, consistent top-percentile PSLE results, and Chinese-medium education focus make it a draw for parent-buyers in the north-east corridor. The surrounding D19 estate is served by Buangkok MRT (NEL) and offers a mix of 99-year leasehold new launches at OCR-competitive price points — popular with families planning Phase 2B school registration.",
  },
  // ── D5 Buona Vista / Clementi / Dover — tertiary & international ──────────
  {
    slug: "national-university-of-singapore",
    label: "National University of Singapore (NUS)",
    level: "tertiary",
    districts: ["D5"],
    description:
      "The National University of Singapore (NUS) at Kent Ridge is Singapore's flagship university. Its Kent Ridge and one-north campuses anchor strong, year-round rental demand from postgraduate students, faculty, and research staff — making D5 condos near NUS a perennial pick for yield-focused investors.",
  },
  {
    slug: "singapore-polytechnic",
    label: "Singapore Polytechnic",
    level: "tertiary",
    districts: ["D5"],
    description:
      "Singapore Polytechnic sits on Dover Road beside Dover MRT (EWL). As one of Singapore's largest polytechnics, its sizeable student and staff population sustains steady rental demand across the Dover, Clementi, and one-north residential belt in D5.",
  },
  {
    slug: "uwcsea-dover",
    label: "United World College of South East Asia (Dover)",
    level: "international",
    districts: ["D5"],
    description:
      "United World College of South East Asia (UWCSEA) Dover Campus is one of Singapore's largest international schools. Its expatriate-family catchment drives premium rental demand for D5 condos around Dover and Clementi — a long-standing favourite for relocating families seeking proximity to the campus.",
  },
  // ── D23 Bukit Batok / Bukit Panjang — international & pre-university ──────
  {
    slug: "dulwich-college",
    label: "Dulwich College (Singapore)",
    level: "international",
    districts: ["D23"],
    description:
      "Dulwich College (Singapore) in Bukit Batok is a leading British international school. Its expat-family enrolment supports rental demand for D23 condos across the Bukit Batok and Hillview area, within easy reach of the campus and the Downtown Line.",
  },
  {
    slug: "millennia-institute",
    label: "Millennia Institute",
    level: "jc",
    districts: ["D23"],
    description:
      "Millennia Institute in Bukit Batok is Singapore's only centralised institute, offering a three-year pre-university programme. Its location anchors student and family demand for D23 condos along the Bukit Batok and Bukit Panjang corridor.",
  },
  // ── D20 Ang Mo Kio / Bishan — polytechnic ───────────────────────────────
  {
    slug: "nanyang-polytechnic",
    label: "Nanyang Polytechnic",
    level: "tertiary",
    districts: ["D20"],
    description:
      "Nanyang Polytechnic on Ang Mo Kio Avenue 8 is a major polytechnic in the north. Its large student population and proximity to the established Ang Mo Kio and Bishan estates sustain rental demand for D20 condos along the NSL and CCL corridor.",
  },
  // ── D22 Jurong / Boon Lay / Lakeside — curated set ──────────────────────
  {
    slug: "canadian-international-school",
    label: "Canadian International School (Lakeside)",
    level: "international",
    districts: ["D22"],
    description:
      "Canadian International School (Lakeside Campus) sits beside Jurong Lake Gardens in Jurong West. As a major international school, its expatriate-family catchment drives rental demand for D22 condos near Lakeside and the emerging Jurong Lake District.",
  },
  {
    slug: "fuhua-primary",
    label: "Fuhua Primary School",
    level: "primary",
    districts: ["D22"],
    description:
      "Fuhua Primary School is located in Jurong East (D22), within the Jurong Lake District growth corridor. Its 1-km priority catchment supports family demand for nearby D22 condos as Singapore's second CBD takes shape around Jurong East interchange.",
  },
  {
    slug: "yuhua-primary",
    label: "Yuhua Primary School",
    level: "primary",
    districts: ["D22"],
    description:
      "Yuhua Primary School sits in Jurong East, within the Jurong Lake District catchment. Its 1-km priority zone makes D22 condos around Jurong East attractive to families banking on the area's long-term URA transformation and second-CBD plans.",
  },
  {
    slug: "jurong-pioneer-junior-college",
    label: "Jurong Pioneer Junior College",
    level: "jc",
    districts: ["D22"],
    description:
      "Jurong Pioneer Junior College in Jurong West is the west's main junior college, formed from the merger of Jurong and Pioneer JCs. Its catchment adds pre-university school demand to the D22 Jurong residential belt and the Jurong Lake District.",
  },
];

export function getSchool(slug: string): SchoolHub | null {
  return SCHOOLS.find((s) => s.slug === slug) ?? null;
}

export function isSchoolSlug(slug: string): string | null {
  if (!slug.startsWith("near-") || slug.endsWith("-mrt")) return null;
  return slug.replace(/^near-/, "");
}

// ─── Static simple hubs ───────────────────────────────────────────────────────

export const SIMPLE_HUBS = [
  "freehold",
  "99-year-leasehold",
  "under-1-5-million",
  "under-2-million",
  "under-3-million",
] as const;

export type SimpleHub = (typeof SIMPLE_HUBS)[number];

export function isSimpleHub(slug: string): slug is SimpleHub {
  return (SIMPLE_HUBS as readonly string[]).includes(slug);
}

// ─── All known hub slugs (for generateStaticParams + sitemap) ─────────────────

export function allHubSlugs(): string[] {
  const top = TOP_YEARS.map((y) => `top-${y}`);
  const mrt = MRT_STATIONS.map((s) => `near-${s.slug}-mrt`);
  const school = SCHOOLS.map((s) => `near-${s.slug}`);
  return [...top, ...mrt, ...school, ...SIMPLE_HUBS];
}
